import React from 'react';
import { Link } from 'react-router-dom';

export type HubCardItem = {
  to?: string;
  title: string;
  description?: string;
  accent?: 'amber' | 'sky' | 'yellow' | 'violet' | 'cyan' | 'emerald' | 'slate';
  /** Carte visible mais non cliquable (ex. fonction référent manquante). */
  disabled?: boolean;
  disabledHint?: string;
};

const accentTitle: Record<NonNullable<HubCardItem['accent']>, string> = {
  amber: 'text-amber-200',
  sky: 'text-sky-200',
  yellow: 'text-yellow-200',
  violet: 'text-violet-200',
  cyan: 'text-cyan-200',
  emerald: 'text-emerald-200',
  slate: 'text-slate-200',
};

const accentBorder: Record<NonNullable<HubCardItem['accent']>, string> = {
  amber: 'hover:border-amber-500/45',
  sky: 'hover:border-sky-500/45',
  yellow: 'hover:border-yellow-500/45',
  violet: 'hover:border-violet-500/45',
  cyan: 'hover:border-cyan-500/45',
  emerald: 'hover:border-emerald-500/45',
  slate: 'hover:border-slate-500/40',
};

const cardShell =
  'rounded-xl border border-[#243052]/90 bg-[#0d152b]/75 p-5 backdrop-blur-sm transition-all';

type PanelHubGridProps = {
  /** Titre optionnel — le layout affiche déjà le nom de section dans le header. */
  title?: string;
  description?: string;
  cards: HubCardItem[];
  columns?: 1 | 2;
  footer?: React.ReactNode;
};

/** Grille de cartes pour hubs panel (CM, admin, modo, scénariste, référents…). */
const PanelHubGrid: React.FC<PanelHubGridProps> = ({ title, description, cards, columns = 2, footer }) => {
  if (!cards.length) {
    return (
      <div className={`${cardShell} border-dashed p-8 text-center text-sm text-slate-400`}>
        Aucun outil disponible avec vos droits actuels.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {title || description ? (
        <div>
          {title ? <h1 className="text-xl font-bold tracking-tight text-white lg:hidden">{title}</h1> : null}
          {description ? (
            <p className={`max-w-2xl text-sm leading-relaxed text-slate-400 ${title ? 'mt-2' : ''}`}>{description}</p>
          ) : null}
        </div>
      ) : null}

      <div className={columns === 2 ? 'grid gap-4 sm:grid-cols-2' : 'grid gap-4'}>
        {cards.map((c) => {
          const a = c.accent || 'slate';
          const content = (
            <>
              <div className={`text-sm font-semibold ${c.disabled ? 'text-slate-500' : accentTitle[a]}`}>{c.title}</div>
              {c.description ? (
                <p className="mt-2 text-xs leading-relaxed text-slate-400">{c.description}</p>
              ) : null}
              {c.disabled && c.disabledHint ? (
                <p className="mt-2 text-[11px] text-slate-600">{c.disabledHint}</p>
              ) : c.to ? (
                <span className="mt-4 inline-flex items-center gap-1 text-[11px] font-medium text-slate-300 group-hover:text-white">
                  Ouvrir
                  <span aria-hidden>→</span>
                </span>
              ) : null}
            </>
          );

          if (c.disabled || !c.to) {
            return (
              <div
                key={c.title}
                className={`${cardShell} cursor-not-allowed border-[#1f2a44]/70 opacity-75`}
                title={c.disabledHint}
              >
                {content}
              </div>
            );
          }

          return (
            <Link
              key={c.to + c.title}
              to={c.to}
              className={`group ${cardShell} hover:bg-[#111c38]/85 ${accentBorder[a]}`}
            >
              {content}
            </Link>
          );
        })}
      </div>

      {footer ? <div className="pt-1">{footer}</div> : null}
    </div>
  );
};

export default PanelHubGrid;
